/**
 * Skills panel — Phase 3 overlay (DOM).
 * Shown in map scene when state.map.skillsPanelOpen.
 * Also contains the Skills toggle button in .hud-map-buttons.
 * Replaces drawSkillsPanel() + drawSkillsButton() in canvas-render-adapter.
 */

import type { GameStateView, SkillView } from "../../application/ports.js";
import { t } from "../../i18n/index.js";

export interface SkillsPanelCallbacks {
  onSkillUpgrade?: (skillId: number, options?: { all?: boolean }) => void;
  onSkillRefund?: (skillId: number, options?: { all?: boolean }) => void;
  onClose?: () => void;
  onToggle?: () => void;
}

export interface SkillsPanel {
  update(state: GameStateView): void;
  destroy(): void;
}

export function createSkillsPanel(
  container: HTMLElement,
  mapButtonsContainer: HTMLElement,
  cbs: SkillsPanelCallbacks
): SkillsPanel {
  // ── Skills toggle button (in map-buttons bar) ─────────────────
  const toggleBtn = document.createElement("button");
  toggleBtn.type = "button";
  toggleBtn.className = "hud-map-btn hud-map-btn--skills";
  toggleBtn.addEventListener("click", () => cbs.onToggle?.());
  mapButtonsContainer.appendChild(toggleBtn);

  // ── Skills panel modal ────────────────────────────────────────
  const el = document.createElement("div");
  el.className = "hud-skills-panel";
  el.style.display = "none";
  container.appendChild(el);

  // Backdrop (click closes)
  const backdrop = document.createElement("div");
  backdrop.className = "hud-skills-panel__backdrop";
  backdrop.addEventListener("click", () => cbs.onClose?.());
  el.appendChild(backdrop);

  const panel = document.createElement("div");
  panel.className = "hud-skills-panel__panel hud-panel";
  el.appendChild(panel);

  // Header row
  const header = document.createElement("div");
  header.className = "hud-skills-panel__header";
  const headerTitle = document.createElement("span");
  headerTitle.className = "hud-skills-panel__header-title";
  const headerGrace = document.createElement("span");
  headerGrace.className = "hud-skills-panel__header-grace";
  header.appendChild(headerTitle);
  header.appendChild(headerGrace);
  // Close button
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "hud-skills-panel__close";
  closeBtn.textContent = "\u00D7";
  closeBtn.addEventListener("click", () => cbs.onClose?.());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Skills grid
  const grid = document.createElement("div");
  grid.className = "hud-skills-panel__grid";
  panel.appendChild(grid);

  // ── Internal render ──────────────────────────────────────────

  function renderSkillsGrid(skills: SkillView[]): void {
    // Diff: rebuild only if count changed
    const existing = grid.querySelectorAll(".hud-skills-panel__item");
    if (existing.length !== skills.length) {
      grid.innerHTML = "";
      for (const skill of skills) {
        const item = buildSkillItem(skill);
        grid.appendChild(item);
      }
    } else {
      skills.forEach((skill, i) => {
        updateSkillItem(existing[i] as HTMLElement, skill);
      });
    }
  }

  function buildSkillItem(skill: SkillView): HTMLElement {
    const item = document.createElement("div");
    item.className = "hud-skills-panel__item";
    item.dataset.skillId = String(skill.id);

    const nameEl = document.createElement("div");
    nameEl.className = "hud-skills-panel__item-name";

    const lvEl = document.createElement("div");
    lvEl.className = "hud-skills-panel__item-lv";

    const valEl = document.createElement("div");
    valEl.className = "hud-skills-panel__item-val";

    const lockEl = document.createElement("div");
    lockEl.className = "hud-skills-panel__item-lock";

    const actions = document.createElement("div");
    actions.className = "hud-skills-panel__item-actions";

    const minusBtn = document.createElement("button");
    minusBtn.type = "button";
    minusBtn.className = "hud-skills-panel__item-btn hud-skills-panel__item-minus";
    minusBtn.textContent = "-";
    minusBtn.addEventListener("click", (ev) => cbs.onSkillRefund?.(skill.id, { all: ev.shiftKey }));

    const plusBtn = document.createElement("button");
    plusBtn.type = "button";
    plusBtn.className = "hud-skills-panel__item-btn hud-skills-panel__item-plus";
    plusBtn.textContent = "+";
    plusBtn.addEventListener("click", (ev) => cbs.onSkillUpgrade?.(skill.id, { all: ev.shiftKey }));
    actions.appendChild(minusBtn);
    actions.appendChild(plusBtn);

    item.appendChild(nameEl);
    item.appendChild(lvEl);
    item.appendChild(valEl);
    item.appendChild(lockEl);
    item.appendChild(actions);

    updateSkillItem(item, skill);
    return item;
  }

  function updateSkillItem(item: HTMLElement, skill: SkillView): void {
    const nameEl = item.querySelector<HTMLElement>(".hud-skills-panel__item-name");
    const lvEl = item.querySelector<HTMLElement>(".hud-skills-panel__item-lv");
    const valEl = item.querySelector<HTMLElement>(".hud-skills-panel__item-val");
    const lockEl = item.querySelector<HTMLElement>(".hud-skills-panel__item-lock");
    const minusBtn = item.querySelector<HTMLButtonElement>(".hud-skills-panel__item-minus");
    const plusBtn = item.querySelector<HTMLButtonElement>(".hud-skills-panel__item-plus");

    if (nameEl) nameEl.textContent = t(skill.nameKey);
    if (lvEl) lvEl.textContent = t("skills.lvMax", { lv: String(skill.level), max: String(skill.maxLevel) });
    if (valEl) valEl.textContent = t("skills.val", { v: String(skill.effectiveValue) });

    if (lockEl) {
      if (skill.lockReason === "needs-level") {
        lockEl.textContent = t("skills.needLevel", { req: String(skill.nextLevelReq) });
        lockEl.className = "hud-skills-panel__item-lock hud-skills-panel__item-lock--level";
      } else if (skill.lockReason === "needs-points") {
        lockEl.textContent = t("skills.needPoints", { cost: String(skill.nextLevelCost) });
        lockEl.className = "hud-skills-panel__item-lock hud-skills-panel__item-lock--points";
      } else if (skill.lockReason === "maxed") {
        lockEl.textContent = t("skills.maxed");
        lockEl.className = "hud-skills-panel__item-lock hud-skills-panel__item-lock--maxed";
      } else {
        lockEl.textContent = t("skills.costShort", { cost: String(skill.nextLevelCost) });
        lockEl.className = "hud-skills-panel__item-lock hud-skills-panel__item-lock--can";
      }
    }

    const tip = t(skill.descriptionKey);
    item.title = tip;
    if (plusBtn) {
      plusBtn.disabled = !skill.canLearn;
      plusBtn.title = skill.canLearn ? t("skills.addTitle") : tip;
      item.classList.toggle("hud-skills-panel__item--can-learn", skill.canLearn);
    }
    if (minusBtn) {
      minusBtn.disabled = !skill.canRefund;
      minusBtn.title = skill.canRefund ? t("skills.refundTitle") : tip;
      item.classList.toggle("hud-skills-panel__item--can-refund", skill.canRefund);
    }
  }

  function update(state: GameStateView): void {
    const isMap = state.scene === "map";
    // Toggle button visible in map scene
    toggleBtn.style.display = isMap ? "" : "none";
    if (isMap) {
      toggleBtn.textContent = t("skills.button");
    }

    const open = isMap && Boolean(state.map?.skillsPanelOpen);
    el.style.display = open ? "" : "none";
    if (!open) return;

    const skills = state.map?.skills ?? [];
    const pointsLeft = state.map?.pointsLeft ?? 0;
    const pointsTotal = state.map?.pointsTotal ?? pointsLeft;

    headerTitle.textContent = t("skills.title");
    headerGrace.textContent = t("skills.grace", { n: String(pointsLeft), total: String(pointsTotal) });

    renderSkillsGrid(skills);
  }

  function destroy(): void {
    toggleBtn.remove();
    el.remove();
  }

  return { update, destroy };
}
